import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AdminPermissions } from 'protocol/api/auth/dto_auth_pb';

import { LoadingStateType } from '@/shared/config';

import { getMyPermissions, setUserPermissions } from './authSlice.thunks';

type AuthState = {
  isAuthenticated: boolean;
  myPermissions: AdminPermissions | null;
  myPermissionsUserID: string | null;
  myPermissionsLoading: LoadingStateType;
};

const initialState: AuthState = {
  isAuthenticated: false,
  myPermissions: null,
  myPermissionsUserID: null,
  myPermissionsLoading: 'idle',
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    resetMyPermissions: (state) => {
      state.myPermissions = null;
      state.myPermissionsUserID = null;
      state.myPermissionsLoading = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMyPermissions.pending, (state, action) => {
        state.myPermissionsUserID = action.meta.arg.UserID ?? null;
        state.myPermissionsLoading = 'pending';
      })
      .addCase(getMyPermissions.fulfilled, (state, action) => {
        // Ignore responses of superseded requests, e.g. a previous user's
        if ((action.meta.arg.UserID ?? null) !== state.myPermissionsUserID) {
          return;
        }

        state.myPermissions = action.payload.Permissions?.Admin ?? null;
        state.myPermissionsLoading = 'succeeded';
      })
      .addCase(getMyPermissions.rejected, (state, action) => {
        if ((action.meta.arg.UserID ?? null) !== state.myPermissionsUserID) {
          return;
        }

        state.myPermissions = null;
        state.myPermissionsLoading = 'failed';
      })
      .addCase(setUserPermissions.fulfilled, (state, action) => {
        // Editing own permissions on the account page applies them immediately
        if ((action.meta.arg.UserID ?? null) !== state.myPermissionsUserID) {
          return;
        }

        state.myPermissions = action.payload.Permissions?.Admin ?? null;
      });
  },
});

export const { actions } = authSlice;

export default authSlice.reducer;
